import { CASH_COVER_KINDS, type CashCoverKind, type Cashflow } from '@epc/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

/** Кассовые разрывы ДДС: период, нехватка, чем покрыто и во сколько обходится (ФН-14) */
export function CashGaps({ c, projectId }: { c: Cashflow; projectId?: string }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    kind: 'credit' as CashCoverKind,
    amount: '',
    dateFrom: '',
    dateTo: '',
    rate: '',
    note: '',
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['finance', 'cashflow'] });
  const add = useMutation({
    mutationFn: () =>
      api('/finance/cash-covers', {
        method: 'POST',
        body: {
          projectId: projectId ?? null,
          kind: f.kind,
          amount: num(f.amount),
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          ratePercent: f.rate.trim() ? num(f.rate) : 0,
          note: f.note.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setF({ ...f, amount: '', rate: '', note: '' });
      refresh();
      toast('Покрытие сохранено');
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/finance/cash-covers/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const ready = num(f.amount) > 0 && f.dateFrom && f.dateTo && f.dateTo >= f.dateFrom;
  return (
    <Panel title="Кассовые разрывы">
      <div className="flex flex-col gap-3 p-5">
        {!c.gaps.length ? (
          <p className="text-[14px] text-ink-2">
            В показанных периодах денег хватает: нарастающий итог не уходит ниже нуля.
          </p>
        ) : (
          c.gaps.map((g) => (
            <div key={g.start} className="rounded-md border border-line p-3 text-[14px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">
                  {date(g.start)} — {date(g.end)}
                </span>
                {g.uncovered > 0 ? (
                  <Badge tone="danger">Не покрыто {tenge(g.uncovered)}</Badge>
                ) : (
                  <Badge tone="success">Покрыто</Badge>
                )}
              </div>
              <div className="mt-1 text-ink-2">
                Нехватка до {tenge(g.deficit)}, покрыто {tenge(g.covered)}
                {g.cost ? `, стоимость финансирования ${tenge(g.cost)}` : ''}
              </div>
              {g.covers.map((v) => (
                <div key={v.id} className="mt-1 flex items-center justify-between gap-2 text-[13px]">
                  <span>
                    {CASH_COVER_KINDS[v.kind]}: {tenge(v.amount)}, {date(v.dateFrom)} — {date(v.dateTo)}
                    {v.ratePercent ? `, ${v.ratePercent} % годовых` : ''}
                    {v.note ? ` · ${v.note}` : ''}
                  </span>
                  {c.canCover ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Убрать покрытие"
                      onClick={() => del.mutate(v.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        )}
        {c.covers.length > c.gaps.reduce((a, g) => a + g.covers.length, 0) ? (
          <p className="text-[13px] text-ink-2">
            Есть покрытия вне разрывов:{' '}
            {c.covers
              .filter((v) => !c.gaps.some((g) => g.covers.some((x) => x.id === v.id)))
              .map((v) => `${CASH_COVER_KINDS[v.kind]} ${tenge(v.amount)}`)
              .join(', ')}
            .
          </p>
        ) : null}
        {c.canCover ? (
          <form
            className="grid gap-3 border-t border-line pt-3 md:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) add.mutate();
            }}
          >
            <Field label="Чем покрываем" htmlFor="cg-kind">
              <Select
                id="cg-kind"
                value={f.kind}
                onChange={(e) => setF({ ...f, kind: e.target.value as CashCoverKind })}
              >
                {(Object.keys(CASH_COVER_KINDS) as CashCoverKind[]).map((k) => (
                  <option key={k} value={k}>
                    {CASH_COVER_KINDS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Сумма, ₸" htmlFor="cg-amount">
              <Input
                id="cg-amount"
                inputMode="decimal"
                value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
              />
            </Field>
            <Field
              label="Ставка, % годовых"
              htmlFor="cg-rate"
              hint="Для переноса платежей и своих средств можно 0"
            >
              <Input
                id="cg-rate"
                inputMode="decimal"
                value={f.rate}
                onChange={(e) => setF({ ...f, rate: e.target.value })}
              />
            </Field>
            <Field label="С" htmlFor="cg-from">
              <Input
                id="cg-from"
                type="date"
                value={f.dateFrom}
                onChange={(e) => setF({ ...f, dateFrom: e.target.value })}
              />
            </Field>
            <Field label="По" htmlFor="cg-to">
              <Input
                id="cg-to"
                type="date"
                value={f.dateTo}
                onChange={(e) => setF({ ...f, dateTo: e.target.value })}
              />
            </Field>
            <Field label="Пояснение" htmlFor="cg-note">
              <Input id="cg-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            </Field>
            <div className="md:col-span-3">
              <Button type="submit" disabled={!ready || add.isPending}>
                Добавить покрытие
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}
